import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, requete, type Definitions, type EtatParite, type Indicateurs, type Moi } from './api';
import { Filtres, type EtatFiltres } from './Filtres';
import { bornes, dateCourte, dateHeure, nombre, pourcent } from './format';
import { IcAlerte, IcComparer, IcTelecharger } from './icones';

/* ------------------------------------------------------------------ définition repliable */
function Def({ cle, defs }: { cle: string; defs: Definitions }) {
  const d = defs[cle];
  if (!d) return null;
  return (
    <details className="def">
      <summary aria-label={`Définition : ${d.libelle}`}>Définition</summary>
      <p>{d.definition}</p>
    </details>
  );
}

/* ------------------------------------------------------------------ parcours des contacts (élément central) */
interface Etape { cle: string; libelle: string; valeur: number; taux?: { cle: string; valeur: number | null }; testid: string; signal?: boolean }

function Parcours({ ind, defs }: { ind: Indicateurs; defs: Definitions }) {
  const etapes: Etape[] = [
    ...(ind.volumes.cibles != null ? [{ cle: 'cibles', libelle: 'Ciblés', valeur: ind.volumes.cibles, testid: 'tuile-cibles' }] : []),
    { cle: 'destinataires', libelle: 'Destinataires', valeur: ind.volumes.destinataires, testid: 'tuile-destinataires' },
    { cle: 'contactes', libelle: 'Contactés', valeur: ind.volumes.contactes, testid: 'tuile-contactes' },
    { cle: 'ouverture', libelle: 'Ouvreurs', valeur: ind.engagement.ouvreurs, taux: { cle: 'ouverture', valeur: ind.taux.ouverture }, testid: 'tuile-ouverture' },
    { cle: 'clic', libelle: 'Cliqueurs', valeur: ind.engagement.cliqueurs, taux: { cle: 'clic', valeur: ind.taux.clic }, testid: 'tuile-clic' },
    { cle: 'reponse', libelle: 'Répondants', valeur: ind.reponses.humains, taux: { cle: 'reponse', valeur: ind.taux.reponse }, testid: 'tuile-reponse' },
    { cle: 'positif', libelle: 'Positifs', valeur: ind.reponses.positives, taux: { cle: 'positif', valeur: ind.taux.positif }, testid: 'tuile-positif', signal: true },
  ];
  const max = Math.max(1, ...etapes.map((e) => e.valeur));
  const H = 120;
  const h = (v: number) => (v <= 0 ? 2 : Math.max(5, (H - 8) * (v / max)));
  // Ruban : chaque étape est un palier, relié au suivant par une courbe.
  let chemin = '';
  etapes.forEach((e, i) => {
    const x0 = i * 100, x1 = x0 + 70, hh = h(e.valeur) / 2;
    if (i === 0) chemin += `M ${x0} ${H / 2 - hh} `;
    chemin += `L ${x1} ${H / 2 - hh} `;
    const suivante = etapes[i + 1];
    if (suivante) { const hs = h(suivante.valeur) / 2; chemin += `C ${x1 + 18} ${H / 2 - hh}, ${x1 + 12} ${H / 2 - hs}, ${x0 + 100} ${H / 2 - hs} `; }
  });
  for (let i = etapes.length - 1; i >= 0; i--) {
    const x0 = i * 100, x1 = x0 + 70, hh = h(etapes[i]!.valeur) / 2;
    if (i === etapes.length - 1) chemin += `L ${x1} ${H / 2 + hh} `;
    chemin += `L ${x0} ${H / 2 + hh} `;
    const precedente = etapes[i - 1];
    if (precedente) { const hp = h(precedente.valeur) / 2; chemin += `C ${x0 - 12} ${H / 2 + hh}, ${x0 - 18} ${H / 2 + hp}, ${x0 - 30} ${H / 2 + hp} `; }
  }
  chemin += 'Z';
  const dernier = etapes.length - 1;

  return (
    <section className="parcours" aria-label="Parcours des contacts" style={{ ['--n' as string]: etapes.length }}>
      <h2>Parcours des contacts</h2>
      {etapes.map((e, i) => (
        <div key={e.cle} className={`etape-haut ${e.signal ? 'signal' : ''}`} style={{ gridColumn: i + 1, ['--i' as string]: i }} data-testid={e.testid}>
          <span className="etape-libelle">{e.libelle}</span>
          <strong className="etape-valeur tuile-valeur">{nombre(e.valeur)}</strong>
          <span className="etape-barre" aria-hidden="true"><span style={{ width: `${Math.max(1, (100 * e.valeur) / max)}%` }} /></span>
        </div>
      ))}
      <svg className="ruban" viewBox={`0 0 ${(etapes.length - 1) * 100 + 70} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="ruban-degrade" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" className="ruban-debut" />
            <stop offset={String((dernier - 0.4) / dernier)} className="ruban-milieu" />
            <stop offset="1" className="ruban-fin" />
          </linearGradient>
        </defs>
        <path d={chemin} fill="url(#ruban-degrade)" />
      </svg>
      {etapes.map((e, i) => (
        <div key={e.cle} className={`etape-bas ${e.signal ? 'signal' : ''}`} style={{ gridColumn: i + 1, ['--i' as string]: i }}>
          <span className="etape-taux">{e.taux ? <><b>{pourcent(e.taux.valeur)}</b> {defs[e.taux.cle]?.libelle.toLowerCase()}</> : null}</span>
          <Def cle={e.taux?.cle ?? e.cle} defs={defs} />
        </div>
      ))}
    </section>
  );
}

/* ------------------------------------------------------------------ parité */
function Parite({ moi, surBascule }: { moi: Moi; surBascule: () => void }) {
  const [etat, setEtat] = useState<EtatParite | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const charger = useCallback(() => api.parite().then(setEtat).catch(() => setEtat(null)), []);
  useEffect(() => { void charger(); }, [charger]);
  const admin = moi.utilisateur.role === 'admin';

  async function comparer() {
    setOccupe(true); setMessage(null);
    try {
      const r = await api.lancerParite();
      setMessage(r.indisponible ? `Comparaison impossible : ${r.indisponible}` : r.identique ? `Identique à l'ancien portail (${r.comparaisons} comparaisons).` : 'Écarts trouvés : détail ci-dessous.');
      await charger();
    } catch (e) {
      setMessage(e instanceof ErreurApi ? e.message : 'Comparaison impossible pour le moment.');
    } finally { setOccupe(false); }
  }
  async function basculer() {
    if (!window.confirm('Basculer le tableau de bord sur la nouvelle application ? Le retour reste possible à tout moment.')) return;
    setOccupe(true);
    try { await api.basculer('tableau_de_bord', 'v2'); setMessage('Tableau de bord basculé sur la nouvelle application.'); surBascule(); }
    catch (e) { setMessage(e instanceof ErreurApi ? e.message : 'Bascule impossible.'); }
    finally { setOccupe(false); }
  }

  if (!etat) return null;
  const d = etat.derniers[0];
  return (
    <section className={`parite ${etat.condition_remplie ? 'ok' : ''}`} aria-label="Parité avec l'ancien portail" data-testid="parite">
      <div className="parite-texte">
        <strong>Parité avec l'ancien portail : {etat.jours_consecutifs} / {etat.requis} jours identiques</strong>
        <span className="pips" aria-hidden="true">
          {Array.from({ length: etat.requis }, (_, i) => <i key={i} className={i < etat.jours_consecutifs ? 'plein' : ''} />)}
        </span>
        <p className="aide">
          Comparée chaque matin aux chiffres de l'ancien portail ; bascule possible après {etat.requis} jours consécutifs identiques.
          {d && ` Dernière comparaison le ${dateHeure(d.compare_le)} : ${d.indisponible ? 'indisponible' : d.identique ? 'identique' : `${d.nb_ecarts} écart(s)`}.`}
        </p>
        {etat.ecarts_recents && !d?.identique && (
          <details className="def">
            <summary>Écarts du {etat.ecarts_recents.jour}</summary>
            <ul className="ecarts">
              {etat.ecarts_recents.ecarts.map((e, i) => (
                <li key={i}>{e.comparaison ? `${e.comparaison} — ` : ''}{e.chemin} : ancien {JSON.stringify(e.ancien)}, v2 {JSON.stringify(e.v2)}</li>
              ))}
            </ul>
          </details>
        )}
        {message && <p role="status" className="parite-message">{message}</p>}
      </div>
      {admin && (
        <div className="parite-actions">
          <button className="secondaire" disabled={occupe} onClick={comparer}><IcComparer />Comparer maintenant</button>
          {etat.condition_remplie && <button className="principal" disabled={occupe} onClick={basculer}>Basculer sur la v2</button>}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ signaux (alertes) */
function Signaux({ alertes }: { alertes: Indicateurs['alertes'] }) {
  const [tout, setTout] = useState(false);
  const ordre = { critique: 0, attention: 1, info: 2 } as const;
  const triees = [...alertes].sort((a, b) => ordre[a.niveau] - ordre[b.niveau]);
  const visibles = tout ? triees : triees.slice(0, 4);
  return (
    <section className="panneau signaux" aria-label="Alertes">
      <h2><IcAlerte />Signaux <span className="compte">{alertes.length}</span></h2>
      {alertes.length === 0 && <p className="aide">Aucun signal sur cette période.</p>}
      <ul>
        {visibles.map((a, i) => <li key={i} className={`alerte ${a.niveau}`}>{a.texte}</li>)}
      </ul>
      {triees.length > 4 && (
        <button className="lien" onClick={() => setTout((x) => !x)}>{tout ? 'Réduire' : `Voir les ${triees.length - 4} autres`}</button>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ réponses par catégorie */
function Reponses({ r }: { r: Indicateurs['reponses'] }) {
  const parts = [
    { cle: 'positives', libelle: 'Positives', n: r.positives },
    { cle: 'neutres', libelle: 'Neutres', n: r.neutres },
    { cle: 'negatives', libelle: 'Négatives', n: r.negatives },
    { cle: 'autres', libelle: 'Autres', n: r.autres },
    { cle: 'non_classees', libelle: 'Non classées', n: r.non_classees },
    { cle: 'automatiques', libelle: 'Automatiques', n: r.automatiques },
  ];
  const total = parts.reduce((a, p) => a + p.n, 0);
  return (
    <section className="panneau" aria-label="Réponses">
      <h2>Réponses <span className="compte">{nombre(total)}</span></h2>
      <div className="empile" aria-hidden="true">
        {parts.filter((p) => p.n > 0).map((p) => <span key={p.cle} className={`part ${p.cle}`} style={{ flexGrow: p.n }} />)}
      </div>
      <ul className="legende">
        {parts.map((p) => <li key={p.cle}><i className={`part ${p.cle}`} />{p.libelle}<b>{nombre(p.n)}</b></li>)}
      </ul>
    </section>
  );
}

function Relances({ r }: { r: NonNullable<Indicateurs['relances']> }) {
  return (
    <section className="panneau" aria-label="Relances">
      <h2>Relances{r.nom ? ` — ${r.nom}` : ''}</h2>
      <dl className="chiffres">
        <div><dt>Envoyées</dt><dd>{nombre(r.envoyees)}</dd></div>
        <div><dt>Ouvreurs</dt><dd>{nombre(r.ouvreurs)}</dd></div>
        <div><dt>Échecs</dt><dd className={r.echecs > 0 ? 'mauvais' : ''}>{nombre(r.echecs)}</dd></div>
        <div><dt>Programmées</dt><dd>{nombre(r.programmees)}</dd></div>
      </dl>
      {r.prochaine && <p className="aide">Prochaine relance : {dateHeure(r.prochaine)}</p>}
    </section>
  );
}

const STATUTS: Record<string, string> = { sent: 'Envoyée', paused: 'En pause', draft: 'Brouillon', active: 'En cours', running: 'En cours', completed: 'Terminée', scheduled: 'Planifiée' };
const Statut = ({ s }: { s: string | null }) => (s ? <span className={`statut statut-${s}`}>{STATUTS[s] ?? s}</span> : null);

function Taux({ v, mauvais }: { v: number | null; mauvais?: boolean }) {
  return (
    <span className={`taux ${mauvais ? 'mauvais' : ''}`}>
      {pourcent(v)}
      <span className="mini" aria-hidden="true"><span style={{ width: `${Math.min(100, v ?? 0)}%` }} /></span>
    </span>
  );
}

/* ------------------------------------------------------------------ page */
export function TableauDeBord({ moi, filtres, surFiltres, surBascule, versCampagne }: {
  moi: Moi; filtres: EtatFiltres; surFiltres: (f: EtatFiltres) => void; surBascule: () => void; versCampagne: (id: number) => void;
}) {
  const [donnees, setDonnees] = useState<{ indicateurs: Indicateurs; definitions: Definitions } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [charge, setCharge] = useState(false);
  const p = bornes(filtres.preset, filtres.du, filtres.au);
  const f = { campagne: filtres.campagne, du: p.du, au: p.au };
  const cle = JSON.stringify(f);

  useEffect(() => {
    let vivant = true;
    setCharge(true); setErreur(null);
    api.indicateurs(JSON.parse(cle)).then((r) => { if (vivant) setDonnees(r); })
      .catch((e) => { if (vivant) setErreur(e instanceof ErreurApi && e.statut === 400 ? `Filtre refusé : ${e.message}` : 'Indicateurs indisponibles pour le moment.'); })
      .finally(() => { if (vivant) setCharge(false); });
    return () => { vivant = false; };
  }, [cle]);

  const ind = donnees?.indicateurs;
  const defs = donnees?.definitions ?? {};
  return (
    <div className={`tableau ${charge ? 'en-calcul' : ''}`}>
      <header className="entete-page avec-actions">
        <div>
          <h1>Tableau de bord</h1>
          <p className="aide">{ind?.perimetre.campaign_id != null ? (ind.perimetre.nom ?? `Campagne ${ind.perimetre.campaign_id}`) : 'Toutes les campagnes'}</p>
        </div>
        <a className="bouton secondaire" href={`/api/v2/indicateurs/export.csv${requete(f)}`} download data-testid="export-indicateurs"><IcTelecharger />Exporter (CSV)</a>
      </header>
      <Parite moi={moi} surBascule={surBascule} />
      <Filtres valeur={filtres} surChangement={surFiltres} note="La période porte sur la date d'envoi du mail de campagne (heure de Paris)." />
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {ind && (
        <>
          <Parcours ind={ind} defs={defs} />

          <section className="volumes" aria-label="Volumes d'envoi">
            <div className="volume" data-testid="tuile-envoyes">
              <span>Mails envoyés</span><strong className="tuile-valeur">{nombre(ind.volumes.envoyes)}</strong>
              <small>{nombre(ind.volumes.en_attente)} en attente, {nombre(ind.volumes.echecs)} en échec</small>
              <Def cle="envoyes" defs={defs} />
            </div>
            <div className={`volume ${(ind.taux.rebond ?? 0) > 5 ? 'mauvais' : ''}`} data-testid="tuile-rebond">
              <span>Taux de rebond</span><strong className="tuile-valeur">{pourcent(ind.taux.rebond)}</strong>
              <small>{nombre(ind.volumes.rebonds)} rebond(s), seuil critique 5 %</small>
              <Def cle="rebond" defs={defs} />
            </div>
            <div className={`volume ${(ind.taux.desinscription ?? 0) > 1 ? 'mauvais' : ''}`} data-testid="tuile-desinscription">
              <span>Désinscription</span><strong className="tuile-valeur">{pourcent(ind.taux.desinscription)}</strong>
              <small>{nombre(ind.desinscriptions.n)} désinscrit(s)</small>
              <Def cle="desinscription" defs={defs} />
            </div>
            <div className="volume" data-testid="tuile-delivrabilite">
              <span>Délivrabilité</span><strong className="tuile-valeur">{pourcent(ind.taux.delivrabilite)}</strong>
              <small>{nombre(ind.volumes.delivres)} mail(s) délivré(s)</small>
              <Def cle="delivrabilite" defs={defs} />
            </div>
          </section>

          <div className="deux-colonnes">
            <div className="colonne-large">
              {ind.campagnes && (
                <section className="panneau" aria-label="Campagnes">
                  <h2>Campagnes <span className="compte">{ind.campagnes.length}</span></h2>
                  <div className="defile">
                    <table data-testid="table-campagnes">
                      <thead><tr><th>Campagne</th><th className="n">Envoyés</th><th className="n">Contactés</th><th>Ouverture</th><th>Réponse</th><th>Rebond</th><th className="n">Désinscr.</th><th>Dernier envoi</th></tr></thead>
                      <tbody>
                        {ind.campagnes.map((c) => (
                          <tr key={c.id}>
                            <td><button className="lien nom-campagne" onClick={() => versCampagne(c.id)}>{c.nom}</button><Statut s={c.statut} /></td>
                            <td className="n">{nombre(c.envoyes)}</td>
                            <td className="n">{nombre(c.contactes)}</td>
                            <td><Taux v={c.taux.ouverture} /></td>
                            <td><Taux v={c.taux.reponse} /></td>
                            <td><Taux v={c.taux.rebond} mauvais={(c.taux.rebond ?? 0) > 5} /></td>
                            <td className="n">{pourcent(c.taux.desinscription)}</td>
                            <td className="date" title={dateHeure(c.dernier)}>{dateCourte(c.dernier)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
              {ind.boites.length > 0 && (
                <section className="panneau" aria-label="Boîtes d'envoi">
                  <h2>Boîtes d'envoi <span className="compte">{ind.boites.length}</span></h2>
                  <div className="defile">
                    <table>
                      <thead><tr><th>Boîte</th><th className="n">Envoyés</th><th>Rebond (repère 5 %)</th><th>Ouverture</th><th className="n">Répondants</th></tr></thead>
                      <tbody>
                        {ind.boites.slice(0, 50).map((b) => {
                          const mauvais = (b.taux_rebond ?? 0) > 5 && b.envoyes >= 20;
                          return (
                            <tr key={`${b.id}`} className={mauvais ? 'a-surveiller' : ''}>
                              <td>{b.id == null ? 'Boîte non renseignée' : b.email}</td>
                              <td className="n">{nombre(b.envoyes)}</td>
                              <td>
                                <span className={`jauge ${mauvais ? 'mauvais' : ''}`}>
                                  <span className="jauge-piste" aria-hidden="true"><span style={{ width: `${Math.min(100, (b.taux_rebond ?? 0) * 5)}%` }} /><i /></span>
                                  {pourcent(b.taux_rebond)}
                                </span>
                              </td>
                              <td>{pourcent(b.ouverture)}</td>
                              <td className="n">{nombre(b.repondants)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
            <div className="colonne-etroite">
              <Signaux alertes={ind.alertes} />
              <Reponses r={ind.reponses} />
              {ind.relances && <Relances r={ind.relances} />}
            </div>
          </div>
          <p className="aide pied">Premier envoi : {dateHeure(ind.volumes.premier)}. Dernier envoi : {dateHeure(ind.volumes.dernier)}.</p>
        </>
      )}
    </div>
  );
}
